import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getData, postData, serverURL } from "../../../../Services/Fetchnodeservices";

// ─────────────────────────────────────────────────────────────────────────────
// API FUNCTIONS (Using your real API)
// ─────────────────────────────────────────────────────────────────────────────

// Helper to make API calls with your existing getData function
const apiGetUsers = async ({ page, perPage, search }) => {
  try {
    // Fetch all users from your API
    const response = await getData("users/Users");
    let users = response.data || [];
    
    // Transform API data to match your component's expected format
    const transformedUsers = users.map((user, index) => {
      // ✅ FIXED IMAGE PATH - According to your folder structure: public/public/uploadnew/upload/
      let imageUrl = null;
      if (user.pic && user.pic.trim() !== "") {
        // Remove any existing path prefixes if present
        let cleanPic = user.pic;
        // If pic already has the full path, extract just the filename
        if (cleanPic.includes('/')) {
          cleanPic = cleanPic.split('/').pop();
        }
        // Construct the correct path
        imageUrl = `${serverURL}/public/uploadnew/upload/${cleanPic}`;
        console.log("Image URL constructed:", imageUrl);
      }
      
      return {
        id: user.id.toString(),
        sn: index + 1,
        status: user.status === "1" ? "Active" : user.status === "0" ? "Inactive" : "Blocked",
        date: user.created_at ? user.created_at.split(' ')[0] : new Date().toISOString().split('T')[0],
        name: user.name || "Unknown",
        contact: user.phone || "N/A",
        wallet: `₹${parseFloat(user.wallet || 0).toLocaleString()}`,
        vip: user.is_vip === "1" ? "Yes" : "No",
        avatar: imageUrl,
        email: user.email,
        latitude: user.latitude,
        longitude: user.longitude,
        address: user.address,
        rawWallet: user.wallet,
        rawStatus: user.status,
        rawVip: user.is_vip,
        fcmtoken: user.fcmtoken,
        myreferal: user.myreferal,
        refer_by: user.refer_by
      };
    });
    
    // Apply search filter
    let filteredUsers = [...transformedUsers];
    if (search) {
      const searchLower = search.toLowerCase();
      filteredUsers = filteredUsers.filter(u => 
        u.name.toLowerCase().includes(searchLower) ||
        u.contact.includes(search) ||
        u.email?.toLowerCase().includes(searchLower) ||
        u.id.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply pagination
    const start = (page - 1) * perPage;
    const paginatedUsers = filteredUsers.slice(start, start + perPage);
    
    return { data: paginatedUsers, total: filteredUsers.length };
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

// Update user via API
const apiUpdateUser = async (id, payload) => {
  console.log("Updating user:", id, payload);
  return { success: true };
};

// Delete users via API
const apiDeleteUsers = async (ids) => {
  console.log("Deleting users:", ids);
  return { success: true };
};

// Get single user details
const apiGetUser = async (id) => {
  try {
    const response = await getData(`users/User/${id}`);
    return response.data || null;
  } catch (error) {
    console.error("Error fetching user:", error);
    return null;
  }
};

// Get user orders
const apiGetUserOrders = async (userId) => {
  try {
    const response = await postData("users/ordersbyid", {
      userid: userId
    });
    
    // Handle different response formats
    if (response.status === true && response.data) {
      // If response.data is an array with total_orders object
      if (response.data.length > 0 && response.data[0].total_orders !== undefined) {
        // This is the total count response
        return { 
          orders: [], 
          totalOrders: response.data[0].total_orders 
        };
      }
      // If response.data is an array of orders
      return { 
        orders: response.data, 
        totalOrders: response.data.length 
      };
    }
    return { orders: [], totalOrders: 0 };
  } catch (error) {
    console.error("Error fetching user orders:", error);
    throw error;
  }
};

// Get user complaints
const apiGetUserComplaints = async (userId) => {
  // TODO: Implement complaints API
  return [];
};

// Update complaint status
const apiUpdateComplaintStatus = async (complaintId, status) => {
  console.log("Updating complaint:", complaintId, status);
  return { success: true };
};

// Send push notification to single user
const apiSendPushNotification = async (userId, title, body, data = {}) => {
  console.log("Sending notification to:", userId, { title, body });
  return { success: true };
};

// Send bulk push notification
const apiSendBulkNotification = async (userIds, title, body, data = {}) => {
  console.log("Sending bulk notification to:", userIds, { title, body });
  return { success: true };
};

// ─────────────────────────────────────────────────────────────────────────────
// ICONS (optimized sizes)
// ─────────────────────────────────────────────────────────────────────────────
const EyeIcon = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>);
const EditIcon = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);
const SearchIcon = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>);
const BellIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>);
const SunIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/></svg>);
const LogoutIcon = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>);
const ChevronLeftIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>);
const ChevronRightIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>);
const TrashIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>);
const RefreshIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>);
const SpinnerIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" style={{ animation: "spin 0.8s linear infinite", transformOrigin: "center" }}/></svg>);
const ShoppingBagIcon = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>);
const MenuIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>);

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

// ✅ SMART AVATAR COMPONENT - Handles image errors and shows initials fallback
const SmartAvatar = ({ name, src, size = 28, theme, borderColor }) => {
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const initials = name?.split(" ").map(w => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
  const colors = ["#3b4a6b", "#2d4a5a", "#4a3b6b", "#3b5a4a", "#5a3b4a", "#4a4a3b"];
  const colorIndex = (name?.charCodeAt(0) || 0) % colors.length;
  const finalBorderColor = borderColor || (theme === 'dark' ? "#2a3145" : "#e5e7eb");

  if (!src || error) {
    return (
      <div style={{ 
        width: size, 
        height: size, 
        borderRadius: "50%", 
        background: colors[colorIndex], 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        fontSize: size * 0.34, 
        fontWeight: 700, 
        color: "#fff", 
        fontFamily: "'DM Sans', sans-serif", 
        flexShrink: 0, 
        border: `1px solid ${finalBorderColor}` 
      }}>
        {initials}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      onLoad={() => setIsLoading(false)}
      onError={(e) => {
        console.error("Image failed to load:", src);
        setError(true);
        setIsLoading(false);
      }}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        objectFit: "cover",
        border: `1px solid ${finalBorderColor}`,
        flexShrink: 0,
        display: isLoading ? "none" : "block"
      }}
    />
  );
};

const Avatar = ({ name = "?", size = 28, theme }) => {
  const initials = name.split(" ").map(w => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const colors = ["#3b4a6b", "#2d4a5a", "#4a3b6b", "#3b5a4a", "#5a3b4a", "#4a4a3b"];
  const colorIndex = (name.charCodeAt(0) || 0) % colors.length;
  const borderColor = theme === 'dark' ? "#2a3145" : "#e5e7eb";
  return (<div style={{ width: size, height: size, borderRadius: "50%", background: colors[colorIndex], display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.34, fontWeight: 700, color: "#94a3b8", fontFamily: "'DM Sans', sans-serif", flexShrink: 0, border: `1px solid ${borderColor}` }}>{initials || "?"}</div>);
};

const StatusBadge = ({ status, theme }) => {
  const config = { 
    Active: { bg: "rgba(20,184,166,0.15)", color: "#14b8a6", border: "rgba(20,184,166,0.3)", dot: "#14b8a6" }, 
    Inactive: { bg: "rgba(148,163,184,0.12)", color: "#94a3b8", border: "rgba(148,163,184,0.2)", dot: "#94a3b8" }, 
    Blocked: { bg: "rgba(239,68,68,0.12)", color: "#ef4444", border: "rgba(239,68,68,0.25)", dot: "#ef4444" } 
  };
  const c = config[status] || config.Inactive;
  return (<div style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "2px 8px 2px 6px", borderRadius: "16px", background: c.bg, border: `1px solid ${c.border}` }}><div style={{ width: "4px", height: "4px", borderRadius: "50%", background: c.dot }} /><span style={{ fontSize: "9px", fontWeight: 600, color: c.color, fontFamily: "'DM Sans', sans-serif" }}>{status}</span></div>);
};

const Checkbox = ({ checked, indeterminate, onChange, theme }) => {
  const borderColor = checked || indeterminate ? "#3b82f6" : (theme === 'dark' ? "#3a4460" : "#d1d5db");
  return (<div onClick={onChange} style={{ width: "14px", height: "14px", borderRadius: "3px", flexShrink: 0, border: `2px solid ${borderColor}`, background: checked || indeterminate ? "#3b82f6" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.15s ease" }}>{indeterminate && !checked ? <div style={{ width: "7px", height: "2px", background: "#fff", borderRadius: "1px" }} /> : checked ? <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg> : null}</div>);
};

const ColHeader = ({ children, theme, width }) => {
  const color = theme === 'dark' ? "#3b82f6" : "#4f46e5";
  const borderColor = theme === 'dark' ? "#1e2740" : "#e5e7eb";
  return (<th style={{ padding: "10px 8px", textAlign: "left", fontSize: "10px", fontWeight: 700, letterSpacing: "0.4px", color: color, fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap", background: "transparent", borderBottom: `1px solid ${borderColor}`, userSelect: "none", width: width || "auto" }}>{children}</th>);
};

const SkeletonRow = ({ cols, theme }) => {
  const bgGradient = theme === 'dark' 
    ? "linear-gradient(90deg, #1e2740 25%, #253050 50%, #1e2740 75%)"
    : "linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)";
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} style={{ padding: "10px 8px" }}>
          <div
            style={{
              height: "10px",
              borderRadius: "4px",
              background: bgGradient,
              backgroundSize: "200% 100%",
              animation: "shimmer 1.4s infinite",
              width: i === 0 ? "14px" : i === 4 ? "28px" : "60%",
            }}
          />
        </td>
      ))}
    </tr>
  );
};

const Toast = ({ message, type, onClose, theme }) => {
  const bg = type === "error" ? (theme === 'dark' ? "#1a0a0a" : "#fef2f2") : (theme === 'dark' ? "#0a1a0a" : "#f0fdf4");
  const border = type === "error" ? "rgba(239,68,68,0.4)" : "rgba(16,185,129,0.4)";
  const textColor = type === "error" ? (theme === 'dark' ? "#fca5a5" : "#dc2626") : (theme === 'dark' ? "#6ee7b7" : "#059669");
  return (<div style={{ position: "fixed", bottom: "24px", right: "24px", zIndex: 999, background: bg, border: `1px solid ${border}`, borderRadius: "8px", padding: "10px 16px", display: "flex", alignItems: "center", gap: "8px", boxShadow: theme === 'dark' ? "0 8px 32px rgba(0,0,0,0.5)" : "0 8px 32px rgba(0,0,0,0.1)", animation: "slideUp 0.25s ease", maxWidth: "300px" }}><div style={{ width: "6px", height: "6px", borderRadius: "50%", background: type === "error" ? "#ef4444" : "#10b981", flexShrink: 0 }} /><span style={{ fontSize: "12px", color: textColor, fontFamily: "'DM Sans', sans-serif", flex: 1 }}>{message}</span><span onClick={onClose} style={{ cursor: "pointer", color: "#64748b", fontSize: "14px", lineHeight: 1 }}>×</span></div>);
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const UsersTable = ({ isDark = true }) => {
  const navigate = useNavigate();
  const theme = isDark ? 'dark' : 'light';
  
  // Theme-specific colors
  const colors = {
    background: isDark ? "#0d1117" : "#f9fafb",
    surface: isDark ? "#141824" : "#ffffff",
    surfaceHover: isDark ? "rgba(255,255,255,0.025)" : "rgba(0,0,0,0.02)",
    border: isDark ? "#1e2740" : "#e5e7eb",
    borderLight: isDark ? "#1e2740" : "#f3f4f6",
    textPrimary: isDark ? "#f1f5f9" : "#111827",
    textSecondary: isDark ? "#94a3b8" : "#6b7280",
    textMuted: isDark ? "#64748b" : "#9ca3af",
    inputBg: isDark ? "#141824" : "#f9fafb",
    inputBorder: isDark ? "#1e2740" : "#e5e7eb",
    selectedBg: isDark ? "rgba(59,130,246,0.07)" : "rgba(79,70,229,0.05)",
    buttonPrimaryBg: isDark ? "#1e3a8a" : "#eef2ff",
    buttonPrimaryColor: isDark ? "#93c5fd" : "#4f46e5",
    modalBg: isDark ? "#141824" : "#ffffff",
    modalBorder: isDark ? "#1e2740" : "#e5e7eb",
    deleteBg: isDark ? "rgba(239,68,68,0.15)" : "#fef2f2",
    deleteColor: "#ef4444",
  };

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [viewUser, setViewUser] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const showToast = (message, type = "success") => { setToast({ message, type }); setTimeout(() => setToast(null), 3500); };

  // Navigation function for edit
  const handleEditNavigation = (user) => {
    navigate("/Dashboard/useredit", {
      state: {
        user: user
      }
    });
  };

  useEffect(() => { const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 500); return () => clearTimeout(t); }, [search]);

  const fetchUsers = useCallback(async () => {
    setLoading(true); 
    setError(null);
    try {
      const res = await apiGetUsers({ page, perPage, search: debouncedSearch });
      setUsers(res.data || []); 
      setTotal(res.total || 0);
    } catch (err) { 
      setError(err.message); 
      showToast(`Failed to load users: ${err.message}`, "error"); 
    } finally { 
      setLoading(false); 
    }
  }, [page, perPage, debouncedSearch]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const mappedUsers = useMemo(() => users, [users]);
  const totalPages = Math.ceil(total / perPage);
  const pageIds = mappedUsers.map(u => u.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  const somePageSelected = pageIds.some(id => selected.has(id)) && !allPageSelected;

  const toggleAll = () => { setSelected(prev => { const next = new Set(prev); allPageSelected ? pageIds.forEach(id => next.delete(id)) : pageIds.forEach(id => next.add(id)); return next; }); };
  const toggleOne = (id) => { setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const clearSelection = () => setSelected(new Set());

  const handleDeleteSelected = async () => { 
    setActionLoading(true); 
    try { 
      await apiDeleteUsers(Array.from(selected)); 
      showToast(`${selected.size} user(s) deleted`); 
      clearSelection(); 
      setConfirmDelete(false); 
      fetchUsers(); 
    } catch (err) { 
      showToast(`Delete failed: ${err.message}`, "error"); 
    } finally { 
      setActionLoading(false); 
    } 
  };
  
  const openView = async (user) => { 
    setViewUser(user); 
    try { 
      const full = await apiGetUser(user.id); 
      if (full) setViewUser(full); 
    } catch {} 
  };

  const handleViewOrders = (user) => {
    navigate("/Dashboard/OrderHistory", { 
      state: { 
        user: {
          id: user.id,
          name: user.name,
          contact: user.contact,
          email: user.email,
          rawStatus: user.rawStatus,
          rawVip: user.rawVip,
          address: user.address,
          wallet: user.wallet
        }
      }
    });
  };

  // Column widths - optimized to prevent horizontal scroll
  const columnWidths = {
    checkbox: "32px",
    sn: "40px",
    status: "75px",
    date: "85px",
    pic: "44px",
    name: "120px",
    contact: "95px",
    wallet: "80px",
    vip: "50px",
    orders: "65px",
    actions: "60px"
  };

  return (
    <div style={{ minHeight: "100vh", background: colors.background, fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0}} @keyframes spin { to { transform: rotate(360deg); } } @keyframes slideUp { from{transform:translateY(16px);opacity:0} to{transform:translateY(0);opacity:1} } input::placeholder { color: ${isDark ? "#3a4a6b" : "#9ca3af"}; }`}</style>

      {/* Top Nav - Compact */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", height: "48px", background: colors.background, borderBottom: `1px solid ${colors.border}`, position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ width: "28px", height: "28px", borderRadius: "6px", border: `1px solid ${colors.border}`, background: colors.surface, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: colors.textSecondary }}><MenuIcon /></div>
          <div style={{ background: "#1e3a8a", borderRadius: "4px", padding: "2px 8px" }}><span style={{ fontSize: "12px", fontWeight: 700, color: "#fff" }}>Users</span></div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{ position: "relative", cursor: "pointer", color: colors.textSecondary }}><BellIcon /><div style={{ position: "absolute", top: "-5px", right: "-7px", background: "#ef4444", borderRadius: "10px", padding: "1px 4px", fontSize: "8px", fontWeight: 700, color: "#fff" }}>8</div></div>
          <div style={{ cursor: "pointer", color: colors.textSecondary }}><SunIcon /></div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}><div style={{ width: "28px", height: "28px", borderRadius: "50%", background: "linear-gradient(135deg,#6c63ff,#4fd1c5)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 700, color: "#fff" }}>AD</div><div><p style={{ margin: 0, fontSize: "11px", fontWeight: 700, color: colors.textPrimary }}>Admin</p><p style={{ margin: 0, fontSize: "9px", color: colors.textMuted }}>Administrator</p></div></div>
          <div style={{ cursor: "pointer", color: colors.textMuted }}><LogoutIcon /></div>
        </div>
      </div>

      {/* Page Body */}
      <div style={{ padding: "16px 20px" }}>
        {/* Search + Action Buttons - Compact */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "14px", flexWrap: "wrap" }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "10px", background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: "8px", padding: "8px 14px" }}>
            <span style={{ color: "#3b82f6", flexShrink: 0 }}><SearchIcon /></span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users..." style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontSize: "12px", color: colors.textPrimary, fontFamily: "'DM Sans', sans-serif", caretColor: "#3b82f6" }} />
            {search && <div onClick={() => setSearch("")} style={{ cursor: "pointer", color: colors.textMuted, fontSize: "16px", lineHeight: 1 }}>×</div>}
          </div>
          <button onClick={fetchUsers} disabled={loading} style={{ padding: "0 14px", borderRadius: "8px", border: `1px solid ${colors.border}`, background: colors.surface, color: loading ? colors.textMuted : colors.textSecondary, cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "5px", fontSize: "11px" }}><RefreshIcon /> Refresh</button>
        </div>

        {error && (<div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: "8px", padding: "8px 14px", marginBottom: "12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}><span style={{ fontSize: "11px", color: "#fca5a5" }}>⚠ {error}</span><button onClick={fetchUsers} style={{ fontSize: "11px", color: "#3b82f6", background: "none", border: "none", cursor: "pointer" }}>Retry</button></div>)}

        {selected.size > 0 && (<div style={{ display: "flex", alignItems: "center", gap: "10px", background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: "8px", padding: "6px 14px", marginBottom: "12px" }}><span style={{ fontSize: "11px", color: "#93c5fd", fontWeight: 600 }}>{selected.size} user{selected.size > 1 ? "s" : ""} selected</span><div style={{ flex: 1 }} /><button onClick={clearSelection} style={{ padding: "3px 10px", borderRadius: "5px", border: `1px solid ${colors.border}`, background: colors.surface, color: colors.textSecondary, fontSize: "10px", cursor: "pointer" }}>Clear</button><button onClick={() => setConfirmDelete(true)} style={{ padding: "3px 10px", borderRadius: "5px", border: "1px solid rgba(239,68,68,0.3)", background: colors.deleteBg, color: colors.deleteColor, fontSize: "10px", cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}><TrashIcon /> Delete</button></div>)}

        {/* Users Table - Fixed Layout */}
       <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: "12px", overflow: "hidden" }}>
  <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
      <thead>
        <tr style={{ background: isDark ? "#0f1520" : "#f9fafb" }}>
          <th style={{ padding: "10px 8px", width: columnWidths.checkbox, borderBottom: `1px solid ${colors.border}` }}>
            <Checkbox checked={allPageSelected} indeterminate={somePageSelected} onChange={toggleAll} theme={theme} />
          </th>
          <ColHeader theme={theme} width={columnWidths.sn}>SN</ColHeader>
          <ColHeader theme={theme} width={columnWidths.status}>Status</ColHeader>
          <ColHeader theme={theme} width={columnWidths.date}>Date</ColHeader>
          <ColHeader theme={theme} width={columnWidths.pic}>Pic</ColHeader>
          <ColHeader theme={theme} width={columnWidths.name}>Name</ColHeader>
          <ColHeader theme={theme} width={columnWidths.contact}>Contact</ColHeader>
          <ColHeader theme={theme} width={columnWidths.wallet}>Wallet</ColHeader>
          <ColHeader theme={theme} width={columnWidths.vip}>VIP</ColHeader>
          <ColHeader theme={theme} width={columnWidths.orders}>Orders</ColHeader>
          <ColHeader theme={theme} width={columnWidths.actions}>Actions</ColHeader>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          Array.from({ length: perPage }).map((_, i) => <SkeletonRow key={i} cols={11} theme={theme} />)
        ) : mappedUsers.length === 0 ? (
          <tr>
            <td colSpan="11" style={{ padding: "40px", textAlign: "center", color: colors.textMuted, fontSize: "12px" }}>
              {debouncedSearch ? "No users match your search" : "No users found"}
            </td>
          </tr>
        ) : (
          mappedUsers.map((user, idx) => {
            const isSel = selected.has(user.id); 
            const isHov = hoveredRow === user.id;
            return (
              <tr 
                key={user.id} 
                onMouseEnter={() => setHoveredRow(user.id)} 
                onMouseLeave={() => setHoveredRow(null)} 
                style={{ 
                  background: isSel ? colors.selectedBg : isHov ? colors.surfaceHover : "transparent",
                  borderBottom: idx < mappedUsers.length - 1 ? `1px solid ${colors.borderLight}` : "none" 
                }}
              >
                <td style={{ padding: "10px 8px" }}>
                  <Checkbox checked={isSel} onChange={() => toggleOne(user.id)} theme={theme} />
                </td>
                <td style={{ padding: "10px 8px" }}>
                  <span style={{ fontSize: "11px", color: colors.textMuted }}>{user.sn}</span>
                </td>
                <td style={{ padding: "10px 8px" }}>
                  <StatusBadge status={user.status} theme={theme} />
                </td>
                <td style={{ padding: "10px 8px" }}>
                  <span style={{ fontSize: "10px", color: colors.textMuted, whiteSpace: "nowrap" }}>{user.date}</span>
                </td>
                <td style={{ padding: "10px 8px" }}>
                  <SmartAvatar 
                    name={user.name} 
                    src={user.avatar} 
                    size={28} 
                    theme={theme} 
                    borderColor={colors.border}
                  />
                </td>
                <td style={{ padding: "10px 8px" }}>
                  <span style={{ fontSize: "11.5px", fontWeight: 600, color: colors.textPrimary }}>{user.name}</span>
                </td>
                <td style={{ padding: "10px 8px" }}>
                  <span style={{ fontSize: "11px", color: colors.textSecondary }}>{user.contact}</span>
                </td>
                <td style={{ padding: "10px 8px" }}>
                  <span style={{ fontSize: "11px", fontWeight: 600, color: colors.textPrimary }}>{user.wallet}</span>
                </td>
                <td style={{ padding: "10px 8px" }}>
                  {user.vip === "Yes" ? (
                    <span style={{ 
                      fontSize: "9px", 
                      fontWeight: 700, 
                      color: "#f59e0b", 
                      background: "rgba(245,158,11,0.12)", 
                      border: "1px solid rgba(245,158,11,0.25)", 
                      borderRadius: "4px", 
                      padding: "2px 6px" 
                    }}>
                      YES
                    </span>
                  ) : (
                    <span style={{ fontSize: "11px", color: colors.textMuted }}>No</span>
                  )}
                </td>
                <td style={{ padding: "10px 8px" }}>
                  <button 
                    onClick={() => handleViewOrders(user)} 
                    style={{ 
                      background: "none", 
                      border: "none", 
                      cursor: "pointer", 
                      color: colors.textMuted, 
                      display: "flex", 
                      alignItems: "center", 
                      gap: "4px", 
                      fontSize: "10px" 
                    }}
                  >
                    <ShoppingBagIcon />
                    <span>Orders</span>
                  </button>
                </td>
                <td style={{ padding: "10px 8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div 
                      onClick={() => openView(user)} 
                      onMouseEnter={() => setHoveredBtn(`v-${user.id}`)} 
                      onMouseLeave={() => setHoveredBtn(null)} 
                      style={{ 
                        cursor: "pointer", 
                        color: hoveredBtn === `v-${user.id}` ? "#93c5fd" : colors.textMuted 
                      }}
                    >
                      <EyeIcon />
                    </div>
                    <div 
                      onClick={() => handleEditNavigation(user)} 
                      onMouseEnter={() => setHoveredBtn(`e-${user.id}`)} 
                      onMouseLeave={() => setHoveredBtn(null)} 
                      style={{ 
                        cursor: "pointer", 
                        color: hoveredBtn === `e-${user.id}` ? "#86efac" : colors.textMuted 
                      }}
                    >
                      <EditIcon />
                    </div>
                  </div>
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  </div>

  {/* Pagination - Compact */}
  <div style={{ 
    display: "flex", 
    alignItems: "center", 
    justifyContent: "space-between", 
    padding: "10px 16px", 
    borderTop: `1px solid ${colors.border}`, 
    background: isDark ? "#0f1520" : "#f9fafb" 
  }}>
    <span style={{ fontSize: "10px", color: colors.textMuted }}>
      {loading ? "Loading…" : `Showing ${Math.min((page-1)*perPage+1, total)}–${Math.min(page*perPage, total)} of ${total} users`}
    </span>
    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <button 
        onClick={() => setPage(p => Math.max(1, p-1))} 
        disabled={page === 1 || loading} 
        style={{ 
          width: "26px", 
          height: "26px", 
          borderRadius: "5px", 
          border: `1px solid ${colors.border}`, 
          background: colors.surface, 
          color: page === 1 ? colors.textMuted : colors.textSecondary, 
          cursor: page === 1 ? "not-allowed" : "pointer", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center" 
        }}
      >
        <ChevronLeftIcon />
      </button>
      
      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
        let p; 
        if (totalPages <= 5) {
          p = i + 1;
        } else if (page <= 3) {
          p = i + 1;
        } else if (page >= totalPages - 2) {
          p = totalPages - 4 + i;
        } else {
          p = page - 2 + i;
        }
        return p <= totalPages && (
          <button 
            key={p} 
            onClick={() => setPage(p)} 
            style={{ 
              width: "26px", 
              height: "26px", 
              borderRadius: "5px", 
              border: page === p ? `1px solid #3b82f6` : `1px solid ${colors.border}`, 
              background: page === p ? colors.buttonPrimaryBg : colors.surface, 
              color: page === p ? colors.buttonPrimaryColor : colors.textSecondary, 
              fontSize: "10px", 
              fontWeight: page === p ? 700 : 400, 
              cursor: "pointer" 
            }}
          >
            {p}
          </button>
        );
      })}
      
      <button 
        onClick={() => setPage(p => Math.min(totalPages, p+1))} 
        disabled={page === totalPages || loading} 
        style={{ 
          width: "26px", 
          height: "26px", 
          borderRadius: "5px", 
          border: `1px solid ${colors.border}`, 
          background: colors.surface, 
          color: page === totalPages ? colors.textMuted : colors.textSecondary, 
          cursor: page === totalPages ? "not-allowed" : "pointer", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center" 
        }}
      >
        <ChevronRightIcon />
      </button>
    </div>
  </div>
</div>
      </div>

      {/* View User Modal - Compact */}
      {viewUser && (
        <div onClick={() => setViewUser(null)} style={{ position:"fixed",inset:0,zIndex:200,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center" }}>
          <div onClick={e=>e.stopPropagation()} style={{ background: colors.modalBg, border: `1px solid ${colors.modalBorder}`, borderRadius:"12px", padding:"20px", width:"320px" }}>
            <div style={{ display:"flex",alignItems:"center",gap:"12px",marginBottom:"16px" }}>
              <SmartAvatar 
                name={viewUser.name} 
                src={viewUser.avatar} 
                size={44} 
                theme={theme} 
                borderColor={colors.border}
              />
              <div>
                <h3 style={{ margin:0,fontSize:"15px",fontWeight:700,color: colors.textPrimary }}>{viewUser.name}</h3>
                <p style={{ margin:"2px 0 0",fontSize:"10px",color: colors.textMuted }}>ID: {viewUser.id}</p>
              </div>
            </div>
            {[
              ["Status", <StatusBadge status={viewUser.status} theme={theme} />],
              ["Contact", viewUser.contact],
              ["Email", viewUser.email],
              ["Joined", viewUser.date],
              ["Wallet", viewUser.wallet],
              ["VIP", viewUser.vip]
            ].map(([label,value]) => (
              <div key={label} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 0",borderBottom: `1px solid ${colors.border}` }}>
                <span style={{ fontSize:"10px",color: colors.textMuted }}>{label}</span>
                {typeof value==="string" ? <span style={{ fontSize:"11px",fontWeight:600,color: colors.textPrimary }}>{value}</span> : value}
              </div>
            ))}
            <button onClick={()=>setViewUser(null)} style={{ width:"100%",marginTop:"16px",padding:"8px",borderRadius:"6px",border:"none",background: colors.buttonPrimaryBg,color: colors.buttonPrimaryColor,fontSize:"11px",fontWeight:600,cursor:"pointer" }}>Close</button>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal - Compact */}
      {confirmDelete && (
        <div onClick={()=>setConfirmDelete(false)} style={{ position:"fixed",inset:0,zIndex:300,background:"rgba(0,0,0,0.75)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center" }}>
          <div onClick={e=>e.stopPropagation()} style={{ background: colors.modalBg, border:"1px solid rgba(239,68,68,0.3)",borderRadius:"12px",padding:"20px",width:"300px" }}>
            <div style={{ width:"36px",height:"36px",borderRadius:"10px",background:"rgba(239,68,68,0.12)",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:"12px" }}><TrashIcon /></div>
            <h3 style={{ margin:"0 0 6px",fontSize:"14px",fontWeight:700,color: colors.textPrimary }}>Delete {selected.size} user{selected.size>1?"s":""}?</h3>
            <p style={{ margin:"0 0 16px",fontSize:"11px",color: colors.textMuted,lineHeight:1.5 }}>This action is permanent and cannot be undone.</p>
            <div style={{ display:"flex",gap:"8px" }}>
              <button onClick={()=>setConfirmDelete(false)} style={{ flex:1,padding:"8px",borderRadius:"6px",border:`1px solid ${colors.border}`,background: colors.surface,color: colors.textSecondary,fontSize:"11px",fontWeight:600,cursor:"pointer" }}>Cancel</button>
              <button onClick={handleDeleteSelected} disabled={actionLoading} style={{ flex:1,padding:"8px",borderRadius:"6px",border:"none",background: colors.deleteBg,color: colors.deleteColor,fontSize:"11px",fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:"5px" }}>{actionLoading ? <><SpinnerIcon /> Deleting…</> : "Yes, Delete"}</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={()=>setToast(null)} theme={theme} />}
    </div>
  );
};

export default UsersTable;